import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { FiX, FiZap, FiCheck, FiLoader, FiUpload, FiFile, FiImage, FiTrash2 } from "react-icons/fi";

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const LoaderIcon = FiLoader as React.FC<React.SVGProps<SVGSVGElement>>;
const UploadIcon = FiUpload as React.FC<React.SVGProps<SVGSVGElement>>;
const FileIcon = FiFile as React.FC<React.SVGProps<SVGSVGElement>>;
const ImageIcon = FiImage as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;

// Direction/tone options
const DIRECTION_OPTIONS = [
  { value: "", label: "No specific direction" },
  { value: "more_formal", label: "Make it more formal" },
  { value: "more_casual", label: "Make it more casual" },
  { value: "shorter", label: "Make it shorter & concise" },
  { value: "more_detailed", label: "Add more detail" },
  { value: "highlight_metrics", label: "Emphasize metrics & data" },
];

interface UploadedFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  hasImage: boolean;
  hasText: boolean;
  textPreview?: string;
}

interface ProcessedDocument {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  base64Data?: string;
  extractedText?: string;
  mediaType?: string;
}

// Past tense versions of the first word
const PAST_TENSE_MAP: Record<string, string> = {
  "Analyzing": "Analyzed",
  "Understanding": "Understood",
  "Generating": "Generated",
  "Optimizing": "Optimized",
  "Finalizing": "Finalized",
  "Connecting": "Connected",
  "Fetching": "Fetched",
};

// Convert first word to past tense
const toPastTense = (step: string): string => {
  const firstWord = step.split(" ")[0];
  const pastTense = PAST_TENSE_MAP[firstWord];
  if (pastTense) {
    return step.replace(firstWord, pastTense);
  }
  return step;
};

interface AIRevampModalProps {
  onClose: () => void;
  onRevamp: (params: {
    instructions: string;
    additionalContext?: string;
    direction?: string;
    documents?: ProcessedDocument[];
  }) => void;
  isRevamping?: boolean;
  revampSteps?: string[];
}

/**
 * AIRevampModal - Modal for AI email improvement with loading state
 * Shows chain of thought with sequential loading → checkmark animation
 */
const AIRevampModal: React.FC<AIRevampModalProps> = ({
  onClose,
  onRevamp,
  isRevamping = false,
  revampSteps = [],
}) => {
  const [instructions, setInstructions] = useState("");
  const [additionalContext, setAdditionalContext] = useState("");
  const [direction, setDirection] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [processedDocuments, setProcessedDocuments] = useState<ProcessedDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  // Use index-based completion for sequential animation
  const [completedStepIndex, setCompletedStepIndex] = useState(-1);

  // Reset when revamping starts
  useEffect(() => {
    if (isRevamping) {
      setCompletedStepIndex(-1);
    }
  }, [isRevamping]);

  // Sequential checkmark animation - complete steps one by one from top to bottom
  useEffect(() => {
    if (!isRevamping || revampSteps.length === 0) {
      return;
    }

    // Find the next step to complete (start from 0)
    const nextToComplete = completedStepIndex + 1;

    // If there's a next step to complete
    if (nextToComplete < revampSteps.length) {
      const timer = setTimeout(() => {
        setCompletedStepIndex(nextToComplete);
      }, 3000); // 3 seconds per step

      return () => clearTimeout(timer);
    }
  }, [isRevamping, revampSteps.length, completedStepIndex]);

  // File upload handler
  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    const formData = new FormData();

    for (let i = 0; i < Math.min(files.length, 5); i++) {
      formData.append("files", files[i]);
    }

    try {
      const API_BASE_URL =
        process.env.REACT_APP_API_URL ||
        (window.location.hostname === "localhost"
          ? "http://localhost:4000"
          : "https://api.sendupdates.ai");

      const response = await fetch(`${API_BASE_URL}/api/emails/upload-documents`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const result = await response.json();
      if (result.success) {
        setUploadedFiles((prev) => [...prev, ...result.data.documents]);
        setProcessedDocuments((prev) => [...prev, ...result.data.processedDocuments]);
      }
    } catch (err) {
      console.error("File upload error:", err);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      handleFileUpload(e.dataTransfer.files);
    },
    [handleFileUpload]
  );

  const removeFile = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
    setProcessedDocuments((prev) => prev.filter((d) => d.id !== fileId));
  };

  const handleRevamp = () => {
    if (!instructions.trim() || isRevamping) return;
    setCompletedStepIndex(-1);
    onRevamp({
      instructions,
      additionalContext: additionalContext.trim() || undefined,
      direction: direction || undefined,
      documents: processedDocuments.length > 0 ? processedDocuments : undefined,
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isRevamping) {
      onClose();
    }
  };

  const isStepComplete = (index: number): boolean => {
    return index <= completedStepIndex;
  };

  const isStepError = (step: string): boolean => {
    return step.startsWith("❌");
  };

  return ReactDOM.createPortal(
    <div className="su-modal-overlay" onClick={handleBackdropClick}>
      <div className="su-modal su-ai-revamp-modal">
        {/* Header */}
        <div className="su-modal-header">
          <div className="su-modal-title">
            <ZapIcon />
            <span>AI REVAMP EMAIL</span>
          </div>
          {!isRevamping && (
            <button type="button" className="su-modal-close" onClick={onClose}>
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="su-modal-body">
          {!isRevamping ? (
            <div className="su-revamp-form">
              {/* Instructions */}
              <div className="su-revamp-section">
                <label className="su-compose-label">
                  DESCRIBE HOW TO IMPROVE THE EMAIL
                </label>
                <textarea
                  className="su-revamp-textarea"
                  placeholder="e.g., Make it more professional and concise, add emoji highlights, focus on growth metrics, improve the call to action..."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={3}
                  autoFocus
                  disabled={isRevamping}
                />
              </div>

              {/* Direction selector */}
              <div className="su-revamp-section">
                <label className="su-compose-label">DIRECTION</label>
                <select
                  className="su-revamp-select"
                  value={direction}
                  onChange={(e) => setDirection(e.target.value)}
                >
                  {DIRECTION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Additional context */}
              <div className="su-revamp-section">
                <label className="su-compose-label">
                  ADDITIONAL CONTEXT (OPTIONAL)
                </label>
                <textarea
                  className="su-revamp-textarea su-revamp-context"
                  placeholder="Add any extra context, data, or notes you want the AI to incorporate..."
                  value={additionalContext}
                  onChange={(e) => setAdditionalContext(e.target.value)}
                  rows={2}
                />
              </div>

              {/* File upload */}
              <div className="su-revamp-section">
                <label className="su-compose-label">
                  UPLOAD DOCUMENTS (OPTIONAL)
                </label>
                <p className="su-revamp-hint">
                  Upload PDFs, images, or text files. Images are sent directly to AI for analysis.
                </p>
                <div
                  className={`su-file-dropzone ${dragActive ? "active" : ""} ${isUploading ? "uploading" : ""}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById("file-input")?.click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.txt"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    style={{ display: "none" }}
                  />
                  {isUploading ? (
                    <div className="su-dropzone-content">
                      <LoaderIcon className="su-spinner" />
                      <span>Uploading...</span>
                    </div>
                  ) : (
                    <div className="su-dropzone-content">
                      <UploadIcon />
                      <span>Drop files here or click to upload</span>
                      <span className="su-dropzone-hint">PDF, PNG, JPG, TXT (max 10MB each)</span>
                    </div>
                  )}
                </div>

                {/* Uploaded files list */}
                {uploadedFiles.length > 0 && (
                  <div className="su-uploaded-files">
                    {uploadedFiles.map((file) => (
                      <div key={file.id} className="su-uploaded-file">
                        <span className="su-file-icon">
                          {file.hasImage ? <ImageIcon /> : <FileIcon />}
                        </span>
                        <span className="su-file-name">{file.filename}</span>
                        <span className="su-file-size">{formatFileSize(file.size)}</span>
                        <button
                          type="button"
                          className="su-file-remove"
                          onClick={() => removeFile(file.id)}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="su-revamp-progress">
              <div className="su-revamp-progress-header">
                <LoaderIcon className="su-spinner" />
                <span>REVAMPING YOUR EMAIL...</span>
              </div>
              <div className="su-revamp-steps">
                {revampSteps.map((step, index) => {
                  const isComplete = isStepComplete(index);
                  const isError = isStepError(step);
                  // Convert to past tense when complete
                  const displayText = isComplete && !isError ? toPastTense(step) : step;
                  
                  return (
                    <div key={index} className={`su-revamp-step ${isComplete ? 'completed' : ''}`}>
                      <span className="su-revamp-step-icon">
                        {isError ? (
                          <span className="su-revamp-error-icon">!</span>
                        ) : isComplete ? (
                          <CheckIcon className="su-revamp-check" />
                        ) : (
                          <LoaderIcon className="su-spinner su-revamp-loader" />
                        )}
                      </span>
                      <span className={`su-revamp-step-text ${isError ? 'error' : ''}`}>
                        {displayText}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!isRevamping && (
          <div className="su-modal-footer">
            <button type="button" className="su-btn-secondary" onClick={onClose}>
              CANCEL
            </button>
            <button
              type="button"
              className="su-btn-primary"
              onClick={handleRevamp}
              disabled={!instructions.trim()}
            >
              <ZapIcon />
              <span>REVAMP EMAIL</span>
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AIRevampModal;
